
import { Story, User } from "../types";
import { supabase } from "./supabaseClient";
import { getCachedImage, setCachedImage } from "./imageCache";

const handleSupabaseError = (context: string, error: any) => {
  const errorMsg = error?.message || (typeof error === 'string' ? error : JSON.stringify(error));
  
  if (errorMsg.includes('Failed to fetch')) {
    console.warn(`[Supabase] Mode hors-ligne ou erreur réseau lors de: ${context}.`);
    return null;
  }

  if (errorMsg.includes('statement timeout')) {
    console.warn(`[Supabase] Délai d'attente dépassé lors de: ${context}. Tentative de réduction de la charge.`);
    return null;
  }
  
  console.error(`[Supabase Error] ${context}:`, errorMsg);
  
  // Sanitization de sécurité
  return errorMsg.replace(/AIza[0-9A-Za-z-_]{35}/g, '[HIDDEN_KEY]');
};

/**
 * Charge les images manquantes depuis Supabase et les met en cache IndexedDB.
 * Utilise un traitement par lots (batching) pour éviter les timeouts SQL.
 */
const fetchAndCacheImages = async (ids: string[]): Promise<void> => {
  if (ids.length === 0) return;
  
  // On limite la taille des lots pour éviter les timeouts sur les colonnes JSON
  const BATCH_SIZE = 4; 
  
  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    const batchIds = ids.slice(i, i + BATCH_SIZE);
    
    try {
      const { data, error } = await supabase
        .from('stories')
        .select('id, coverImage:segments->0->>imageUrl')
        .in('id', batchIds);

      if (error) throw error;
      
      if (data) {
        for (const item of data) {
          if (item.id && item.coverImage) {
            await setCachedImage(item.id, item.coverImage);
          }
        }
      }
    } catch (err) {
      handleSupabaseError(`Background image batch fetch (${i / BATCH_SIZE + 1})`, err);
    }
  }
};

/**
 * Enrichit les objets story avec les images du cache local.
 */
const enrichWithPersistentCache = async (metaData: any[]) => {
  const enriched = await Promise.all(metaData.map(async (s) => {
    const cached = await getCachedImage(s.id);
    return { ...s, coverImage: cached || s.coverImage || null };
  }));
  return enriched;
};

// --- Story Management ---

export const saveStoryToDB = async (story: Story): Promise<void> => {
  if (story.segments?.[0]?.imageUrl) {
    await setCachedImage(story.id, story.segments[0].imageUrl);
  }

  try {
    const { error } = await supabase
      .from('stories')
      .upsert({
        id: story.id,
        userId: story.userId,
        title: story.title,
        segments: story.segments,
        createdAt: story.createdAt
      });
    
    if (error) throw error;
  } catch (err) {
    handleSupabaseError("Save Story", err);
  }
};

export const getStoriesByUserPreview = async (userId: string): Promise<any[]> => {
  try {
    const { data: metaData, error: metaError } = await supabase
      .from('stories')
      .select('id, userId, title, createdAt') 
      .eq('userId', userId)
      .order('createdAt', { ascending: false });

    if (metaError) throw metaError;
    if (!metaData) return [];

    const enriched = await enrichWithPersistentCache(metaData);
    
    const missingIds = enriched.filter(s => !s.coverImage).map(s => s.id);
    if (missingIds.length > 0) {
      // Pas de await ici pour ne pas bloquer le rendu UI
      fetchAndCacheImages(missingIds);
    }

    return enriched;
  } catch (err) {
    handleSupabaseError("Fetch User Previews", err);
    return [];
  }
};

export const getStoryById = async (id: string): Promise<Story | null> => {
  try {
    const { data, error } = await supabase
      .from('stories')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    
    if (data?.segments?.[0]?.imageUrl) {
      await setCachedImage(data.id, data.segments[0].imageUrl);
    }

    return data as Story;
  } catch (err) {
    handleSupabaseError("Fetch Full Story", err);
    return null;
  }
};

export const getAllStoriesPreview = async (limit = 12): Promise<any[]> => {
  try {
    const { data: metaData, error: metaError } = await supabase
      .from('stories')
      .select('id, userId, title, createdAt') 
      .order('createdAt', { ascending: false })
      .limit(limit);

    if (metaError) throw metaError;
    if (!metaData) return [];

    const enriched = await enrichWithPersistentCache(metaData);
    
    const missingIds = enriched.filter(s => !s.coverImage).map(s => s.id);
    if (missingIds.length > 0) {
      fetchAndCacheImages(missingIds);
    }

    return enriched;
  } catch (err) {
    handleSupabaseError("Fetch Community Previews", err);
    return [];
  }
};

export const deleteStoryFromDB = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase.from('stories').delete().eq('id', id);
    if (error) throw error;
  } catch (err) {
    handleSupabaseError("Delete Story", err);
  }
};

// --- User Management ---
export const saveUserToDB = async (user: User): Promise<void> => {
  try {
    const { error } = await supabase.from('users').upsert(user);
    if (error) throw error;
  } catch (err) {
    handleSupabaseError("Save User", err);
  }
};

export const getUserFromDB = async (id: string): Promise<User | null> => {
  try {
    const { data, error } = await supabase.from('users').select('*').eq('id', id).maybeSingle();
    if (error) throw error;
    return data as User;
  } catch (err) {
    handleSupabaseError("Get User", err);
    return null;
  }
};

export const findUserByEmail = async (email: string): Promise<User | null> => {
  try {
    const { data, error } = await supabase.from('users').select('*').eq('email', email).maybeSingle();
    if (error) throw error;
    return data as User;
  } catch (err) {
    handleSupabaseError("Find User", err);
    return null;
  }
};

// --- Data Portability ---

export const exportAllData = async (userId: string): Promise<void> => {
  try {
    const { data, error } = await supabase
      .from('stories')
      .select('*')
      .eq('userId', userId);

    if (error) throw error;

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `amadal_stories_${userId}_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    handleSupabaseError("Export Data", err);
  }
};

export const importData = async (file: File, userId: string): Promise<void> => {
  try {
    const text = await file.text();
    const data = JSON.parse(text);
    if (!Array.isArray(data)) throw new Error("Format de fichier invalide.");
    const stories = data.map(s => ({ ...s, userId: userId }));
    const { error } = await supabase.from('stories').upsert(stories);
    if (error) throw error;
  } catch (err) {
    throw new Error(handleSupabaseError("Import Data", err) || "Erreur importation");
  }
};
